import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Link,
  Stack,
  Button,
  Popover,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import logo from "../static/media/logo.webp";

const PRIMARY = "#06BBC7";
const BLACK = "#111827";
const YELLOW = "#F7C948";
const YELLOW_HOVER = "#efbd35";

const Footer = () => {
  const [treatmentsAnchor, setTreatmentsAnchor] = useState(null);

  const handleOpenTreatments = (event) => {
    setTreatmentsAnchor(event.currentTarget);
  };

  const handleCloseTreatments = () => {
    setTreatmentsAnchor(null);
  };

  const treatmentsOpen = Boolean(treatmentsAnchor);

  const navItems = [
    {
      label: "Inicio",
      href: "/",
    },
    {
      label: "Acerca de nosotros",
      href: "/acerca-de-nosotros",
    },
    {
      label: "Tienda",
      href: "/tienda",
    },
  ];

  const treatmentItems = [
    {
      label: "Estética Corporal",
      href: "/tratamientos/estetica-corporal",
    },
    {
      label: "Estética Facial",
      href: "/tratamientos/estetica-facial",
    },
  ];

  const socialItems = [
    {
      label: "Facebook",
      href: "https://www.facebook.com/profile.php?id=100063547994489",
      icon: <FacebookIcon />,
    },
    {
      label: "Instagram",
      href: "https://www.instagram.com/lilyssunflower_",
      icon: <InstagramIcon />,
    },
    {
      label: "WhatsApp",
      href: "https://wa.me/593984781610",
      icon: <WhatsAppIcon />,
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: "#24272c",
        color: "#ffffff",
        pt: { xs: 5, md: 6 },
        pb: { xs: 3, md: 3.5 },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1.2fr 1fr 1fr",
            },
            gap: { xs: 4, md: 5 },
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                backgroundColor: "#ffffff",
                borderRadius: "22px",
                px: { xs: 2.2, md: 2.6 },
                py: { xs: 1.5, md: 1.7 },
                mb: 2,
                boxShadow: "0 18px 38px rgba(0,0,0,0.22)",
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Lily's Sunflowers"
                sx={{
                  width: "auto",
                  height: { xs: 58, md: 70 },
                  display: "block",
                }}
              />
            </Box>

            <Typography
              sx={{
                color: "rgba(255,255,255,0.72)",
                fontSize: "0.95rem",
                lineHeight: 1.7,
                maxWidth: 360,
              }}
            >
              Cuidado estético profesional con atención personalizada para tu
              bienestar, tu piel y tu imagen.
            </Typography>

            <Stack direction="row" spacing={1.1} sx={{ mt: 2.4 }}>
              {socialItems.map((item) => (
                <IconButton
                  key={item.label}
                  component="a"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.label}
                  sx={{
                    width: 38,
                    height: 38,
                    backgroundColor: "#ffffff",
                    color: PRIMARY,
                    border: "1px solid rgba(255,255,255,0.18)",
                    "&:hover": {
                      backgroundColor: PRIMARY,
                      color: "#ffffff",
                    },
                  }}
                >
                  {item.icon}
                </IconButton>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 800,
                mb: 1.5,
              }}
            >
              Navegación
            </Typography>

            <Stack spacing={1}>
              {navItems.map((item) => (
                <Typography
                  key={item.label}
                  component={RouterLink}
                  to={item.href}
                  sx={{
                    color: "rgba(255,255,255,0.72)",
                    fontSize: "0.92rem",
                    fontWeight: 600,
                    lineHeight: 1.5,
                    width: "fit-content",
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": {
                      color: PRIMARY,
                    },
                  }}
                >
                  {item.label}
                </Typography>
              ))}

              <Typography
                component="button"
                onClick={handleOpenTreatments}
                sx={{
                  p: 0,
                  m: 0,
                  border: "none",
                  outline: "none",
                  backgroundColor: "transparent",
                  color: "rgba(255,255,255,0.72)",
                  fontSize: "0.92rem",
                  fontWeight: 600,
                  lineHeight: 1.5,
                  width: "fit-content",
                  textAlign: "left",
                  cursor: "pointer",
                  "&:hover": {
                    color: PRIMARY,
                  },
                }}
              >
                Tratamientos
              </Typography>
            </Stack>

            <Popover
              open={treatmentsOpen}
              anchorEl={treatmentsAnchor}
              onClose={handleCloseTreatments}
              disableScrollLock
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              PaperProps={{
                sx: {
                  mt: -0.5,
                  ml: 1.2,
                  width: 220,
                  borderRadius: "18px",
                  backgroundColor: "#ffffff",
                  border: "1px solid rgba(6,187,199,0.18)",
                  boxShadow: "0 18px 38px rgba(0,0,0,0.18)",
                  overflow: "hidden",
                },
              }}
            >
              <Stack sx={{ p: 1 }}>
                {treatmentItems.map((item) => (
                  <Typography
                    key={item.label}
                    component={RouterLink}
                    to={item.href}
                    onClick={handleCloseTreatments}
                    sx={{
                      px: 1.6,
                      py: 1.15,
                      borderRadius: "12px",
                      color: BLACK,
                      fontSize: "0.92rem",
                      fontWeight: 700,
                      textDecoration: "none",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(6,187,199,0.1)",
                        color: PRIMARY,
                      },
                    }}
                  >
                    {item.label}
                  </Typography>
                ))}
              </Stack>
            </Popover>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 800,
                mb: 1.5,
              }}
            >
              Contacto
            </Typography>

            <Button
              component={RouterLink}
              to="/agendar-cita"
              variant="contained"
              sx={{
                backgroundColor: YELLOW,
                color: BLACK,
                px: 2.8,
                py: 1,
                borderRadius: "999px",
                textTransform: "none",
                fontSize: "0.9rem",
                fontWeight: 800,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: YELLOW_HOVER,
                  boxShadow: "none",
                },
              }}
            >
              Agendar Cita
            </Button>

            <Stack spacing={1.1} sx={{ mt: 2 }}>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.68)",
                  fontSize: "0.9rem",
                  lineHeight: 1.5,
                }}
              >
                Teléfono:{" "}
                <Link
                  href="tel:+593984781610"
                  underline="none"
                  sx={{
                    color: "#ffffff",
                    fontWeight: 700,
                    "&:hover": {
                      color: PRIMARY,
                    },
                  }}
                >
                  +593 98 478 1610
                </Link>
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.68)",
                  fontSize: "0.9rem",
                  lineHeight: 1.5,
                }}
              >
                Correo:{" "}
                <Link
                  href="mailto:estetica@lilysunflowers.com"
                  underline="none"
                  sx={{
                    color: "#ffffff",
                    fontWeight: 700,
                    overflowWrap: "anywhere",
                    "&:hover": {
                      color: PRIMARY,
                    },
                  }}
                >
                  estetica@lilysunflowers.com
                </Link>
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            pt: 2.5,
            borderTop: "1px solid rgba(255,255,255,0.1)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 1.2,
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.64)",
              fontSize: "0.86rem",
              fontWeight: 500,
            }}
          >
            Todos los derechos reservados © 2026
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.64)",
              fontSize: "0.86rem",
              fontWeight: 500,
            }}
          >
            Desarrollado por{" "}
            <Link
              href="https://devzone-ec.com"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                color: "#ffffff",
                fontWeight: 800,
                "&:hover": {
                  color: PRIMARY,
                },
              }}
            >
              DEVZONE
            </Link>{" "}
            &{" "}
            <Link
              href="https://drsolucionesinformaticas.com"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                color: "#ffffff",
                fontWeight: 800,
                "&:hover": {
                  color: PRIMARY,
                },
              }}
            >
              D&amp;R Soluciones Informáticas
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;